import React, { useContext, useEffect, useState, useRef } from "react";
import OfferCard from "../OfferCard/OfferCard";
import { OffersContext } from "../../../context/OffersContext";
import { AuthContext } from "../../../context/AuthContext";
import { fetchPackagesBatch } from "../../../services/PackagesService";
import RegionCountriesModal from "../RegionCountriesModal/RegionCountriesModal";
import LinkButton from "../../Elements/LinkButton/LinkButton";
import EsimCheckModal from "../EsimCheckModal/EsimCheckModal";
import AddedToCartModal from "../AddedToCartModal/AddedToCartModal";
import MainButton from "../../Elements/MainButton/MainButton";
import SecondButton from "../../Elements/SecondButton/SecondButton";
import { useCart } from "../../../context/CartContext";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import QuantityPicker from "../QuantityPicker/QuantityPicker";
import phoneIcon from "../../../assets/icons/package/phone.png";

import "./OffersList.css";

const OffersList = () => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language || "en";

  // Global context
  const { selectedLocationId, selectedLocationType, selectedLocationName } = useContext(OffersContext);
  const { addItems, items: cartItems, removeItem } = useCart();
  const { walletCurrency } = useContext(AuthContext);

  const navigate = useNavigate();
  const listRef = useRef(null);

  // Local state: packages and selection
  const [packages, setPackages] = useState([]);
  const [selectedPackageId, setSelectedPackageId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [quantity, setQuantity] = useState(1);

  // Modal state
  const [isEsimModalOpen, setIsEsimModalOpen] = useState(false);
  const [isAddedModalOpen, setIsAddedModalOpen] = useState(false);
  const [isRegionModalOpen, setIsRegionModalOpen] = useState(false);
  const [pendingOpenModal, setPendingOpenModal] = useState(false);

  // Computed: currently selected package
  const selectedPackage = packages.find((p) => p.id === selectedPackageId) || null;

  const handleQuantityChange = (newQuantity) => {
    setQuantity(newQuantity);
  };

  // Add selected packages to cart (multiple quantity)
  const handleAddToCart = () => {
    if (!selectedPackage || quantity < 1) return;

    const hasTopUpInCart = cartItems.some((item) => item.type === "topup" || item.is_topup);

    if (hasTopUpInCart) {
      alert(t("offers.topUpInCart"));
      return;
    }

    const itemsToAdd = Array.from({ length: quantity }, () => ({
      id: selectedPackage.id,
      internal_package_id: selectedPackage.id,
      name: selectedPackage.name,
      nameLang: i18n.resolvedLanguage,
      price: selectedPackage.price,
      currency: selectedPackage.currency,
      gigabytes: selectedPackage.gigabytes,
      duration: selectedPackage.duration,
      top_up_available: selectedPackage.top_up_available,
      country_id: selectedPackage.country_id,
      region_id: selectedPackage.region_id,
      quantity: 1,
    }));

    addItems(itemsToAdd);
    setPendingOpenModal(true);
  };

  // Add to cart + redirect to checkout
  const handleBuyNow = () => {
    handleAddToCart();
    navigate(`/${currentLang}/cart`);
  };

  // Load packages for selected country/region
  useEffect(() => {
    setSelectedPackageId(null);
    setQuantity(1);

    if (!selectedLocationId) {
      setPackages([]);
      return;
    }

    const load = async () => {
      try {
        setLoading(true);
        setError("");

        const isCountry = selectedLocationType === "country";

        const data = await fetchPackagesBatch({
          sortBy: "gigabytes",
          ascOrder: true,
          limit: 50,
          skip: 0,
          countryId: isCountry ? selectedLocationId : null,
          regionId: !isCountry ? selectedLocationId : null,
          currency: walletCurrency || "EUR",
          language: i18n.resolvedLanguage,
        });

        const items = (data.items || [])
          .filter((pkg) => pkg.active === true)
          .sort((a, b) => {
            // Unlimited (-1 GB) goes last instead of first
            const ga = a.gigabytes === -1 ? Infinity : a.gigabytes;
            const gb = b.gigabytes === -1 ? Infinity : b.gigabytes;
            if (ga === gb) return 0;
            return ga < gb ? -1 : 1;
          });
        setPackages(items);
      } catch (e) {
        setError(t("errors.failedToLoadPackages", "Failed to load packages"));
        setPackages([]);
        setSelectedPackageId(null);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [selectedLocationId, selectedLocationType, walletCurrency, i18n.resolvedLanguage]);

  useEffect(() => {
    if (!pendingOpenModal) return;
    setIsAddedModalOpen(true);
    setPendingOpenModal(false);
  }, [cartItems, pendingOpenModal]);

  // Toggle package selection (single select)
  const togglePackage = (id) => {
    setSelectedPackageId((prev) => (prev === id ? null : id));
  };

  const isRegion = selectedLocationType === "region";

  return (
    <div className="offers-list" ref={listRef}>
      {/* Header: title + region link + eSIM check button */}
      <div className="offers-list-header">
        <div className="offers-list-header-left">
          {isRegion && selectedLocationId && (
            <div className="offers-region-link">
              <LinkButton onClick={() => setIsRegionModalOpen(true)}>{t("offers.viewCountriesInRegion")}</LinkButton>
            </div>
          )}
        </div>

        <button type="button" className="offers-esim-check-btn" onClick={() => setIsEsimModalOpen(true)}>
          <img src={phoneIcon} alt="" className="offers-esim-check-icon" />
          <span>{t("offers.checkEsimDevice")}</span>
        </button>
      </div>

      {/* Loading / Error states */}
      {loading && <p className="text-muted">{t("offers.loadingPackages")}</p>}
      {!loading && !error && selectedLocationId && packages.length === 0 && (
        <p className="text-muted">{t("offers.noPackagesAvailable")}</p>
      )}
      {error && <p className="text-muted">{error}</p>}
      {!loading && !error && !selectedLocationId && <p className="text-muted">{t("offers.selectDestination")}</p>}

      {/* Grid of OfferCards */}
      {!loading && !error && selectedLocationId && packages.length > 0 && (
        <div className="offers-grid-wrapper">
          <div className="offers-grid">
            {packages.map((pkg) => (
              <OfferCard
                key={pkg.id}
                id={pkg.id}
                gigabytes={pkg.gigabytes}
                duration={pkg.duration}
                price={pkg.price}
                currency={pkg.currency}
                selected={selectedPackageId === pkg.id}
                onToggle={() => togglePackage(pkg.id)}
                topUpAvailable={pkg.top_up_available}
              />
            ))}
          </div>
        </div>
      )}

      {/* Action bar: quantity + buttons (when packages available) */}
      {!loading && !error && selectedLocationId && packages.length > 0 && (
        <div className="offers-actions-bar">
          <QuantityPicker value={quantity} onChange={handleQuantityChange} />

          <SecondButton onClick={handleAddToCart} disabled={!selectedPackage}>
            {t("offers.addToCart")}
          </SecondButton>

          <MainButton onClick={handleBuyNow} disabled={!selectedPackage}>
            {t("offers.buyNow")}
          </MainButton>
        </div>
      )}

      {/* Modals */}
      <EsimCheckModal isOpen={isEsimModalOpen} onClose={() => setIsEsimModalOpen(false)} />
      <RegionCountriesModal
        isOpen={isRegionModalOpen}
        onClose={() => setIsRegionModalOpen(false)}
        regionId={selectedLocationId}
        regionName={selectedLocationName}
      />
      <AddedToCartModal
        isOpen={isAddedModalOpen}
        onClose={() => setIsAddedModalOpen(false)}
        onGoToCart={() => {
          setIsAddedModalOpen(false);
          navigate(`/${currentLang}/cart`);
        }}
        onRemoveItem={removeItem}
        items={cartItems}
        currency={walletCurrency || "EUR"}
      />
    </div>
  );
};

export default OffersList;
